import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Square, Loader2, Sparkles, Check, Edit3 } from 'lucide-react';
import axios from 'axios';

const API = '/api';

export const VoiceLogger = ({ onAnalysisComplete, mealType }) => {
  const { authHeaders } = useAuth();
  const [recording, setRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [editing, setEditing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);

  const startRecording = useCallback(async () => {
    setError('');
    setTranscription('');

    // Check for Web Speech API support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Speech recognition not supported in this browser. Please type your meal description instead.');
      setEditing(true);
      return;
    }

    try {
      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setPermissionDenied(true);
      setError('Microphone access denied. Please type your meal description instead.');
      setEditing(true);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    let finalTranscript = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + ' ';
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setTranscription(finalTranscript + interim);

      // Reset silence timer
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        stopRecording();
      }, 3000);
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed') {
        setPermissionDenied(true);
        setError('Microphone access denied.');
      }
      stopRecording();
    };

    recognition.onend = () => {
      setRecording(false);
    };

    recognition.start();
    setRecording(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
    setRecording(false);
  }, []);

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  const handleAnalyze = async () => {
    if (!transcription.trim()) return;
    setAnalyzing(true);
    setError('');
    try {
      const res = await axios.post(`${API}/meals/voice`, {
        transcription: transcription.trim(),
        meal_type: mealType,
      }, authHeaders());
      onAnalysisComplete(res.data);
    } catch (err) {
      setError('Failed to analyze. Please try again.');
      setAnalyzing(false);
    }
  };

  // Waveform visualization bars
  const WaveformBars = () => (
    <div className="flex items-center justify-center gap-1 h-16">
      {Array.from({ length: 24 }).map((_, i) => (
        <div
          key={i}
          className="w-1 rounded-full bg-[#6B9E8A]"
          style={{
            height: recording ? `${20 + Math.random() * 80}%` : '20%',
            transition: 'height 0.15s ease',
            animationDelay: `${i * 50}ms`,
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="animate-fade-in-up" data-testid="voice-logger">
      {/* Recording area */}
      <div className="bg-[#141C22] rounded-2xl p-8 mb-6">
        {/* Mic button */}
        <div className="flex flex-col items-center">
          {!recording && !transcription && !editing ? (
            <>
              <button
                data-testid="start-recording-btn"
                onClick={startRecording}
                className="w-24 h-24 rounded-full bg-[#C67A5C] flex items-center justify-center mb-6 hover:scale-105 active:scale-95 transition-transform shadow-xl shadow-[#C67A5C]/30"
              >
                <Mic className="w-10 h-10 text-white" />
              </button>
              <p className="text-[#F0EDE8] font-medium text-lg">Tap to start</p>
              <p className="text-[#A1A1AA] text-sm mt-1">Describe your meal naturally</p>
              <p className="text-[#A1A1AA]/60 text-xs mt-4 max-w-xs text-center">
                "I had two scrambled eggs with butter, a piece of toast with jelly, and a glass of orange juice"
              </p>
            </>
          ) : recording ? (
            <>
              <WaveformBars />
              <button
                data-testid="stop-recording-btn"
                onClick={stopRecording}
                className="w-20 h-20 rounded-full bg-[#C45B5B] flex items-center justify-center mt-6 mb-4 hover:scale-105 active:scale-95 transition-transform"
              >
                <Square className="w-8 h-8 text-white" fill="white" />
              </button>
              <p className="text-[#F0EDE8] font-medium">Listening...</p>
              {/* Live transcription */}
              {transcription && (
                <p className="text-[#A1A1AA] text-base mt-4 text-center max-w-sm leading-relaxed">{transcription}</p>
              )}
            </>
          ) : null}

          {/* Transcription result / edit */}
          {!recording && (transcription || editing) && (
            <div className="w-full mt-4">
              {editing ? (
                <textarea
                  data-testid="transcription-edit"
                  value={transcription}
                  onChange={e => setTranscription(e.target.value)}
                  placeholder="Describe what you ate..."
                  className="w-full h-32 px-4 py-3 rounded-xl bg-[#1E2A32] border border-white/10 text-[#F0EDE8] text-base resize-none focus:ring-2 focus:ring-[#6B9E8A]/30 focus:outline-none"
                  autoFocus
                />
              ) : (
                <div className="p-4 rounded-xl bg-[#1E2A32] border border-white/10">
                  <p className="text-[#F0EDE8] text-base leading-relaxed">{transcription}</p>
                </div>
              )}
              <div className="flex gap-3 mt-4">
                <button
                  data-testid="edit-transcription-btn"
                  onClick={() => setEditing(!editing)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-[#A1A1AA] hover:text-[#F0EDE8] hover:bg-white/5"
                >
                  <Edit3 className="w-4 h-4" /> {editing ? 'Preview' : 'Edit'}
                </button>
                <button
                  data-testid="re-record-btn"
                  onClick={() => { setTranscription(''); setEditing(false); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-[#A1A1AA] hover:text-[#F0EDE8] hover:bg-white/5"
                >
                  <Mic className="w-4 h-4" /> Re-record
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-xl bg-[#C67A5C]/10 text-[#C67A5C] text-sm">{error}</div>
      )}

      {/* Manual text entry link */}
      {!recording && !transcription && !editing && (
        <button
          data-testid="type-instead-btn"
          onClick={() => setEditing(true)}
          className="w-full text-center text-sm text-[#7A7A72] hover:text-[#1A3A4A] dark:hover:text-[#F0EDE8] font-medium py-2 mb-4"
        >
          Or type your meal description
        </button>
      )}

      {/* Analyze button */}
      {transcription.trim() && !recording && (
        <Button
          data-testid="voice-analyze-btn"
          onClick={handleAnalyze}
          disabled={analyzing}
          className="w-full h-14 rounded-xl bg-[#1A3A4A] dark:bg-[#6B9E8A] text-white font-semibold text-base"
        >
          {analyzing ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analyzing...</>
          ) : (
            <><Sparkles className="w-5 h-5 mr-2" /> Analyze Meal</>
          )}
        </Button>
      )}
    </div>
  );
};
